import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import {
  budgetUsageRowKey,
  formatBudgetCurrency,
  formatBudgetUsageDateTime,
  formatUsagePercent,
  resolveBudgetUsageEntityName,
  sortBudgetUsageEvents,
  usageRiskLevel,
  type BudgetUsageSortDirection,
  type BudgetUsageSortKey,
  type UsageRiskLevel,
} from '@/utils/budgetUsage';
import type { BudgetUsageEvent } from '@/types/budgetUsage';

function ScopeTypeBadge({ scopeType }: { scopeType: string }) {
  const normalized = scopeType.toUpperCase();
  const variant = normalized === 'PORTFOLIO' ? 'neutral' : 'success';
  return <Badge variant={variant}>{normalized || '—'}</Badge>;
}

function UsagePercentBadge({ percent }: { percent: number | null }) {
  if (percent == null || !Number.isFinite(percent)) {
    return <span className="text-foreground-muted">—</span>;
  }
  const risk = usageRiskLevel(percent);
  if (risk === 'high') {
    return (
      <span className="inline-flex items-center rounded border border-error/20 bg-error-subtle px-2 py-0.5 text-xs font-medium text-error-ink">
        {formatUsagePercent(percent)}
      </span>
    );
  }
  const variant: Record<Exclude<UsageRiskLevel, 'high'>, 'success' | 'warning'> = {
    low: 'success',
    medium: 'warning',
  };
  return <Badge variant={variant[risk]}>{formatUsagePercent(percent)}</Badge>;
}

function profileCampaignDetailPath(
  profileId: string,
  campaignId: string,
  adProduct?: string | null
): string {
  const base = `/accounts/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}`;
  const product = adProduct?.toUpperCase();
  if (product && product !== 'SP') {
    return `${base}?product=${encodeURIComponent(product)}`;
  }
  return base;
}

function SortableHeader({
  label,
  sortKey,
  activeSortKey,
  direction,
  align = 'left',
  onSort,
}: {
  label: string;
  sortKey: BudgetUsageSortKey;
  activeSortKey: BudgetUsageSortKey | null;
  direction: BudgetUsageSortDirection;
  align?: 'left' | 'right';
  onSort: (key: BudgetUsageSortKey) => void;
}) {
  const isActive = activeSortKey === sortKey;
  const Icon = !isActive ? ArrowUpDown : direction === 'asc' ? ArrowUp : ArrowDown;

  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide transition-colors hover:text-foreground ${
        isActive ? 'text-brand' : 'text-foreground-muted'
      } ${align === 'right' ? 'ml-auto' : ''}`}
      aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
    </button>
  );
}

const COLUMN_COUNT = 6;

export function BudgetUsageTable({
  events,
  profileId,
  campaignNames,
  portfolioNames,
  linkCampaigns = true,
  emptyMessage = 'No budget usage alerts in this range.',
}: {
  events: BudgetUsageEvent[];
  profileId: string;
  campaignNames: Map<string, string>;
  portfolioNames: Map<string, string>;
  linkCampaigns?: boolean;
  emptyMessage?: string;
}) {
  const [sortKey, setSortKey] = useState<BudgetUsageSortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<BudgetUsageSortDirection>('desc');

  const handleSort = (key: BudgetUsageSortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortKey(key);
    setSortDirection('desc');
  };

  const sortedEvents = useMemo(() => {
    if (!sortKey) return events;
    return sortBudgetUsageEvents(events, sortKey, sortDirection);
  }, [events, sortKey, sortDirection]);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm">
      <div className="max-h-[32rem] overflow-auto custom-scrollbar">
        <table className="min-w-full text-body-sm">
          <thead className="sticky top-0 z-10 border-b border-border bg-surface-muted">
            <tr>
              <th className="sticky left-0 z-20 whitespace-nowrap bg-surface-muted px-4 py-2 text-left text-xs font-bold uppercase tracking-wide text-foreground-muted">
                Time
              </th>
              <th className="sticky left-[160px] z-20 min-w-[220px] bg-surface-muted px-4 py-2 text-left text-xs font-bold uppercase tracking-wide text-foreground-muted">
                Entity
              </th>
              <th className="whitespace-nowrap px-4 py-2 text-left text-xs font-bold uppercase tracking-wide text-foreground-muted">
                Type
              </th>
              <th className="whitespace-nowrap px-4 py-2 text-right">
                <SortableHeader
                  label="Budget"
                  sortKey="budget"
                  activeSortKey={sortKey}
                  direction={sortDirection}
                  align="right"
                  onSort={handleSort}
                />
              </th>
              <th className="whitespace-nowrap px-4 py-2 text-left">
                <SortableHeader
                  label="Usage %"
                  sortKey="usage"
                  activeSortKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                />
              </th>
              <th className="whitespace-nowrap px-4 py-2 text-left text-xs font-bold uppercase tracking-wide text-foreground-muted">
                Ad product
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedEvents.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className="px-4 py-8 text-center text-foreground-muted">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedEvents.map((event, index) => {
                const rowKey = budgetUsageRowKey(event, index);
                const { label, subId } = resolveBudgetUsageEntityName(
                  event,
                  campaignNames,
                  portfolioNames
                );
                const scopeType = String(event.budgetScopeType ?? '').toUpperCase();
                const campaignId = event.campaignId ?? (scopeType === 'CAMPAIGN' ? subId : null);
                const entityCell =
                  linkCampaigns && campaignId ? (
                    <Link
                      to={profileCampaignDetailPath(profileId, campaignId, event.adProduct)}
                      className="block font-bold text-brand hover:underline"
                      title={`${label} (${subId})`}
                    >
                      {label}
                    </Link>
                  ) : (
                    <span className="block font-bold text-foreground" title={`${label} (${subId})`}>
                      {label}
                    </span>
                  );

                return (
                  <tr key={rowKey} className="transition-colors hover:bg-surface-muted/80">
                    <td className="sticky left-0 z-[1] whitespace-nowrap bg-surface px-4 py-3 text-foreground-muted">
                      {formatBudgetUsageDateTime(event.usageUpdatedAt)}
                    </td>
                    <td className="sticky left-[160px] z-[1] min-w-[220px] bg-surface px-4 py-3">
                      {entityCell}
                      <span className="mt-0.5 block font-mono text-[10px] text-foreground-muted">
                        ID: {subId}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <ScopeTypeBadge scopeType={scopeType} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-medium">
                      {formatBudgetCurrency(event.budget)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <UsagePercentBadge percent={event.budgetUsagePercent} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {event.adProduct ? (
                        <Badge variant="neutral">{event.adProduct.toUpperCase()}</Badge>
                      ) : (
                        <span className="text-foreground-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
